/* ============================================
   PostComposer — Full post creation/editing form
   Multi-channel, media, scheduling, preview.
   ============================================ */
import { useState, useEffect, useMemo, useRef } from 'react'
import type { SocialPost, SocialChannel, SocialPostType, SocialStrategy } from '@/lib/types'
import { SOCIAL_POST_TYPES } from '@/lib/types'
import ChannelSelector from '@/components/social-media/ChannelSelector'
import PostPreview from '@/components/social-media/PostPreview'
import { useAuth } from '@/hooks/useAuth'
import { showToast } from '@/components/layout/Toast'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

interface PostComposerProps {
  editPost?: SocialPost | null
  initialDate?: Date | null
  strategy?: SocialStrategy | null
  onSave: (post: Partial<SocialPost>) => void
  onSchedule: (post: Partial<SocialPost>) => void
}

const CHAR_LIMITS: Record<SocialChannel, number> = {
  instagram: 2200,
  twitter: 280,
  facebook: 63206,
  pinterest: 500,
}

const POST_TYPE_LABELS: Record<SocialPostType, string> = {
  image: 'Imagen',
  video: 'Video',
  carousel: 'Carrusel',
  text: 'Texto',
  reel: 'Reel',
  story: 'Story',
}

function formatDateForInput(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  const h = String(date.getHours()).padStart(2, '0')
  const min = String(date.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${d}T${h}:${min}`
}

export default function PostComposer({ editPost, initialDate, strategy, onSave, onSchedule }: PostComposerProps) {
  const { session } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Form state
  const [channels, setChannels] = useState<SocialChannel[]>(editPost?.channels ?? ['instagram'])
  const [caption, setCaption] = useState(editPost?.caption ?? '')
  const [hashtags, setHashtags] = useState<string[]>(editPost?.hashtags ?? [])
  const [hashtagInput, setHashtagInput] = useState('')
  const [postType, setPostType] = useState<SocialPostType>(editPost?.post_type ?? 'image')
  const [scheduledDate, setScheduledDate] = useState(
    editPost?.publication_date
      ? formatDateForInput(new Date(editPost.publication_date))
      : initialDate
        ? formatDateForInput(initialDate)
        : ''
  )
  const [notes, setNotes] = useState(editPost?.notes ?? '')
  const [showPreview, setShowPreview] = useState(false)
  const [previewChannel, setPreviewChannel] = useState<SocialChannel>(channels[0] ?? 'instagram')

  // Media upload states
  const [mediaUrls, setMediaUrls] = useState<string[]>(editPost?.media_urls ?? [])
  const [mediaAltTexts, setMediaAltTexts] = useState<string[]>(editPost?.media_alt_texts ?? [])
  const [isDragActive, setIsDragActive] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<Record<string, 'uploading' | 'error' | 'success'>>({})

  // Sync media when editing post changes
  useEffect(() => {
    if (editPost) {
      setMediaUrls(editPost.media_urls ?? [])
      setMediaAltTexts(editPost.media_alt_texts ?? [])
    }
  }, [editPost])

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragActive(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragActive(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      uploadFiles(e.dataTransfer.files)
    }
  }

  const handleDropzoneClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      uploadFiles(e.target.files)
    }
  }

  const uploadFiles = async (files: FileList) => {
    if (!session?.access_token) {
      showToast('Sesión no encontrada', 'error')
      return
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const tempId = `${file.name}-${Date.now()}`

      setUploadProgress(prev => ({ ...prev, [tempId]: 'uploading' }))

      try {
        const formData = new FormData()
        formData.append('file', file)

        const res = await fetch(
          `${API_URL}/api/social/upload-media`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
            body: formData,
          }
        )

        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body.error || 'Error al subir archivo')
        }

        const data = await res.json()
        setMediaUrls(prev => [...prev, data.url])
        setMediaAltTexts(prev => [...prev, file.name])
        setUploadProgress(prev => ({ ...prev, [tempId]: 'success' }))

        // Auto detect post type
        if (file.type.startsWith('video/')) {
          setPostType('video')
        } else if (file.type.startsWith('image/')) {
          setMediaUrls(currentUrls => {
            if (currentUrls.length > 1) {
              setPostType('carousel')
            } else {
              setPostType('image')
            }
            return currentUrls
          })
        }

        showToast(`Archivo ${file.name} subido`, 'success')
      } catch (err) {
        console.error(err)
        setUploadProgress(prev => ({ ...prev, [tempId]: 'error' }))
        showToast(err instanceof Error ? err.message : 'Error al subir archivo', 'error')
      }
    }
  }

  const removeMedia = (index: number) => {
    setMediaUrls(prev => prev.filter((_, i) => i !== index))
    setMediaAltTexts(prev => prev.filter((_, i) => i !== index))
  }

  // Instagram-specific
  const [igFirstComment, setIgFirstComment] = useState(
    (editPost?.instagram_data?.first_comment as string) ?? ''
  )
  // Pinterest-specific
  const [pinBoard, setPinBoard] = useState(
    (editPost?.pinterest_data?.board as string) ?? ''
  )
  const [pinLink, setPinLink] = useState(
    (editPost?.pinterest_data?.link as string) ?? ''
  )

  // Sync preview channel when channels change
  useEffect(() => {
    if (channels.length > 0 && !channels.includes(previewChannel)) {
      setPreviewChannel(channels[0])
    }
  }, [channels, previewChannel])

  // Character limit for current most-restrictive channel
  const activeLimit = useMemo(() => {
    if (channels.length === 0) return null
    return Math.min(...channels.map(ch => CHAR_LIMITS[ch]))
  }, [channels])

  const captionLength = caption.length
  const isOverLimit = activeLimit !== null && captionLength > activeLimit
  const channelWarnings = channels.filter(ch => captionLength > CHAR_LIMITS[ch])

  // Hashtag suggestions from strategy
  const hashtagSuggestions = useMemo(() => {
    if (!strategy?.hashtag_groups) return []
    const allTags = strategy.hashtag_groups.flatMap(g => g.tags)
    return allTags.filter(t => !hashtags.includes(t))
  }, [strategy, hashtags])

  const addHashtag = (tag: string) => {
    const clean = tag.replace(/^#/, '').trim()
    if (clean && !hashtags.includes(clean)) {
      setHashtags(prev => [...prev, clean])
    }
    setHashtagInput('')
  }

  const removeHashtag = (tag: string) => {
    setHashtags(prev => prev.filter(t => t !== tag))
  }

  const handleHashtagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      if (hashtagInput.trim()) addHashtag(hashtagInput)
    }
  }

  // Build post data
  const buildPostData = (): Partial<SocialPost> => {
    const data: Partial<SocialPost> = {
      channels,
      caption,
      hashtags,
      post_type: postType,
      publication_date: scheduledDate ? new Date(scheduledDate).toISOString() : null,
      notes: notes || null,
      media_urls: mediaUrls,
      media_alt_texts: mediaAltTexts,
    }

    if (channels.includes('instagram') && igFirstComment) {
      data.instagram_data = { ...(editPost?.instagram_data ?? {}), first_comment: igFirstComment }
    }
    if (channels.includes('pinterest')) {
      data.pinterest_data = {
        ...(editPost?.pinterest_data ?? {}),
        board: pinBoard || undefined,
        link: pinLink || undefined,
      }
    }

    if (editPost?.id) data.id = editPost.id

    return data
  }

  const handleSaveDraft = () => {
    onSave({ ...buildPostData(), status: 'draft' })
  }

  const handleSchedule = () => {
    if (!scheduledDate) return
    onSchedule({ ...buildPostData(), status: 'scheduled' })
  }

  const handlePublishNow = () => {
    onSchedule({ ...buildPostData(), status: 'published', publication_date: new Date().toISOString() })
  }

  return (
    <div className="sm-composer">
      <div className="sm-composer__main">
        {/* Header */}
        <div className="sm-composer__header">
          <h2>{editPost ? 'Editar Publicación' : 'Nueva Publicación'}</h2>
        </div>

        {/* Channel selector */}
        <div className="sm-composer__section">
          <label className="form-label">Canales</label>
          <ChannelSelector selected={channels} onChange={setChannels} />
        </div>

        {/* Post type */}
        <div className="sm-composer__section">
          <label className="form-label">Tipo de publicación</label>
          <div className="sm-composer__type-grid">
            {SOCIAL_POST_TYPES.map(type => (
              <button
                key={type}
                type="button"
                className={`sm-composer__type-btn ${postType === type ? 'sm-composer__type-btn--active' : ''}`}
                onClick={() => setPostType(type)}
              >
                {POST_TYPE_LABELS[type]}
              </button>
            ))}
          </div>
        </div>

        {/* Caption editor */}
        <div className="sm-composer__section">
          <label className="form-label">
            <span>Texto de la publicación</span>
            <span className={`sm-composer__char-count ${isOverLimit ? 'sm-composer__char-count--over' : ''}`}>
              {captionLength}{activeLimit !== null ? `/${activeLimit}` : ''}
            </span>
          </label>
          <textarea
            className="form-textarea sm-composer__textarea"
            value={caption}
            onChange={e => setCaption(e.target.value)}
            placeholder="Escribe el contenido de tu publicación..."
            rows={6}
          />
          {channelWarnings.length > 0 && (
            <div className="sm-composer__warning">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              Excede el límite en: {channelWarnings.join(', ')}
            </div>
          )}
        </div>

        {/* Hashtags */}
        <div className="sm-composer__section">
          <label className="form-label">Hashtags</label>
          <div className="sm-composer__hashtags">
            {hashtags.map(tag => (
              <span key={tag} className="sm-composer__hashtag-chip">
                #{tag}
                <button type="button" onClick={() => removeHashtag(tag)} className="sm-composer__hashtag-remove">×</button>
              </span>
            ))}
            <input
              type="text"
              className="sm-composer__hashtag-input"
              value={hashtagInput}
              onChange={e => setHashtagInput(e.target.value)}
              onKeyDown={handleHashtagKeyDown}
              placeholder="Agregar hashtag..."
            />
          </div>
          {/* Suggestions from strategy */}
          {hashtagSuggestions.length > 0 && (
            <div className="sm-composer__suggestions">
              <span className="sm-composer__suggestions-label">Sugerencias:</span>
              {hashtagSuggestions.slice(0, 10).map(tag => (
                <button
                  key={tag}
                  type="button"
                  className="sm-composer__suggestion-btn"
                  onClick={() => addHashtag(tag)}
                >
                  #{tag}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Media upload zone */}
        <div className="sm-composer__section">
          <label className="form-label">Media</label>
          
          <input
            type="file"
            ref={fileInputRef}
            multiple
            onChange={handleFileChange}
            style={{ display: 'none' }}
            accept="image/*,video/*"
          />

          <div 
            className={`sm-composer__dropzone ${isDragActive ? 'sm-composer__dropzone--active' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleDropzoneClick}
            style={{ cursor: 'pointer' }}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4 }}>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <span>Arrastra archivos aquí o haz clic para subir</span>
            <span style={{ fontSize: 11, opacity: 0.6 }}>Imágenes, videos, carruseles (Máx 50MB)</span>
          </div>

          {/* Previews Grid */}
          {(mediaUrls.length > 0 || Object.values(uploadProgress).some(status => status === 'uploading')) && (
            <div className="sm-composer__media-grid" style={{ marginTop: 12 }}>
              {mediaUrls.map((url, idx) => {
                const isVideo = url.endsWith('.mp4') || url.endsWith('.mov') || url.endsWith('.webm') || url.includes('/video_') || url.includes('.mov?') || url.includes('.mp4?') || url.includes('.webm?')
                return (
                  <div key={url} className="sm-composer__media-thumb">
                    {isVideo ? (
                      <video src={url} controls={false} muted style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'var(--radius-md)' }} />
                    ) : (
                      <img src={url} alt={mediaAltTexts[idx] || 'Preview'} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'var(--radius-md)' }} />
                    )}
                    <button
                      type="button"
                      className="sm-composer__media-remove"
                      onClick={(e) => {
                        e.stopPropagation()
                        removeMedia(idx)
                      }}
                      title="Eliminar"
                    >
                      ×
                    </button>
                  </div>
                )
              })}
              
              {/* Uploading states */}
              {Object.entries(uploadProgress)
                .filter(([_, status]) => status === 'uploading')
                .map(([tempId]) => (
                  <div key={tempId} className="sm-composer__media-thumb sm-composer__media-thumb--uploading">
                    <div className="auth-spinner" style={{ width: 24, height: 24, margin: '0 auto 8px auto' }} />
                    <span style={{ fontSize: 11, opacity: 0.7 }}>Subiendo...</span>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Schedule */}
        <div className="sm-composer__section">
          <label className="form-label">Fecha y hora de publicación</label>
          <input
            type="datetime-local"
            className="form-input"
            value={scheduledDate}
            onChange={e => setScheduledDate(e.target.value)}
          />
        </div>

        {/* Platform-specific fields */}
        {channels.includes('instagram') && (
          <div className="sm-composer__section sm-composer__section--platform">
            <label className="form-label" style={{ color: '#E4405F' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E4405F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/>
              </svg>
              Primer comentario (Instagram)
            </label>
            <input
              type="text"
              className="form-input"
              value={igFirstComment}
              onChange={e => setIgFirstComment(e.target.value)}
              placeholder="Hashtags adicionales o CTA en primer comentario..."
            />
          </div>
        )}

        {channels.includes('pinterest') && (
          <div className="sm-composer__section sm-composer__section--platform">
            <label className="form-label" style={{ color: '#E60023' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E60023" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
              </svg>
              Pinterest
            </label>
            <div className="form-row" style={{ gap: 12 }}>
              <input
                type="text"
                className="form-input"
                value={pinBoard}
                onChange={e => setPinBoard(e.target.value)}
                placeholder="Nombre del tablero..."
                style={{ flex: 1 }}
              />
              <input
                type="url"
                className="form-input"
                value={pinLink}
                onChange={e => setPinLink(e.target.value)}
                placeholder="URL de destino..."
                style={{ flex: 1 }}
              />
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="sm-composer__section">
          <label className="form-label">Notas internas</label>
          <textarea
            className="form-textarea"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Notas internas que no se publicarán..."
            rows={2}
          />
        </div>

        {/* Actions */}
        <div className="sm-composer__actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleSaveDraft}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
            </svg>
            Guardar Borrador
          </button>
          <button
            type="button"
            className={`btn ${showPreview ? 'btn-secondary' : 'btn-secondary'}`}
            onClick={() => setShowPreview(!showPreview)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
            </svg>
            {showPreview ? 'Ocultar Preview' : 'Preview'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSchedule}
            disabled={!scheduledDate || channels.length === 0}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            Programar
          </button>
          <button
            type="button"
            className="btn btn-accent"
            onClick={handlePublishNow}
            disabled={channels.length === 0}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
            Publicar Ahora
          </button>
        </div>
      </div>

      {/* Preview panel */}
      {showPreview && channels.length > 0 && (
        <div className="sm-composer__preview">
          <div className="sm-composer__preview-tabs">
            {channels.map(ch => (
              <button
                key={ch}
                type="button"
                className={`sm-composer__preview-tab ${previewChannel === ch ? 'sm-composer__preview-tab--active' : ''}`}
                onClick={() => setPreviewChannel(ch)}
              >
                {ch.charAt(0).toUpperCase() + ch.slice(1)}
              </button>
            ))}
          </div>
          <PostPreview
            caption={caption}
            mediaUrls={mediaUrls}
            channel={previewChannel}
            hashtags={hashtags}
          />
        </div>
      )}
    </div>
  )
}
